import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ClipboardCheck, MapPin, Clock, CalendarDays, Check, X, MinusCircle, CheckCheck } from "lucide-react";
import { fmtDate, fmtTime, initials } from "@/lib/helpers";

export default function Attendance() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState(null);
  const [selected, setSelected] = useState(null);
  const [roster, setRoster] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [markingAll, setMarkingAll] = useState(false);

  const markAll = async () => {
    if (!window.confirm("Mark everyone on this session as present? Certificates will be emailed (except E-learning courses).")) return;
    setMarkingAll(true);
    try {
      const { data } = await api.post(`/sessions/${selected.id}/attendance/mark-all-present`);
      toast.success(`${data.marked} marked present${data.certificates_sent ? ` · ${data.certificates_sent} certificate(s) emailed` : ""}`);
      await loadRoster(selected);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setMarkingAll(false);
  };

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/sessions");
      const mine = user.role === "admin" ? data : data.filter((s) => s.trainer_id === user.id);
      setSessions(mine);
      if (mine.length) selectSession(mine[0]);
      else setSessions(mine);
    })();
  }, []);

  const loadRoster = async (s) => {
    const [enr, cand] = await Promise.all([
      api.get(`/enrollments?session_id=${s.id}`),
      api.get(`/sessions/${s.id}/candidates`).catch(() => ({ data: [] })),
    ]);
    setRoster(enr.data);
    setCandidates(cand.data);
  };

  const selectSession = async (s) => {
    setSelected(s);
    await loadRoster(s);
  };

  const mark = async (enrId, attendance) => {
    try {
      await api.put(`/enrollments/${enrId}/attendance`, { attendance });
      toast.success(`Marked ${attendance}`);
      await loadRoster(selected);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const markCandidate = async (bookingId, attendance) => {
    try {
      const { data } = await api.put(`/sessions/${selected.id}/candidates/${bookingId}/attendance`, { attendance });
      toast.success(data.certificate_sent ? "Marked present — certificate emailed" : `Marked ${attendance}`);
      await loadRoster(selected);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!sessions) return <Loading />;
  if (sessions.length === 0) return (
    <div><PageHeader title="Attendance" subtitle="Record attendance and issue completions." /><EmptyState message="No sessions assigned to you yet." icon={ClipboardCheck} /></div>
  );

  return (
    <div>
      <PageHeader title="Attendance" subtitle="Record attendance — marking present completes the course and issues a certificate." testid="attendance-header">
        <Select value={selected?.id} onValueChange={(v) => selectSession(sessions.find((s) => s.id === v))}>
          <SelectTrigger className="w-[280px]" data-testid="attendance-session-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {sessions.map((s) => <SelectItem key={s.id} value={s.id}>{s.course?.title} · {fmtDate(s.start)}</SelectItem>)}
          </SelectContent>
        </Select>
      </PageHeader>

      {selected && (
        <Card className="p-5 mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
          <h3 className="font-heading font-semibold">{selected.course?.title}</h3>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{fmtDate(selected.start)}</span>
            <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{fmtTime(selected.start)}</span>
            <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{selected.location}</span>
          </div>
          </div>
          {(roster.length > 0 || candidates.length > 0) && (
            <Button onClick={markAll} disabled={markingAll} className="bg-emerald-600 hover:bg-emerald-700" data-testid="attendance-mark-all-present-button">
              <CheckCheck className="h-4 w-4 mr-1.5" />{markingAll ? "Marking..." : "Mark all present"}
            </Button>
          )}
        </Card>
      )}

      <Card className="divide-y divide-border">
        {roster.length === 0 && candidates.length === 0 && <div className="p-8"><EmptyState message="No one has registered for this session yet." icon={ClipboardCheck} /></div>}
        {candidates.map((c) => (
          <div key={c.session_id} className="flex items-center gap-4 p-4" data-testid={`candidate-roster-row-${c.session_id}`}>
            <Avatar className="h-10 w-10"><AvatarFallback className="bg-amber-500/10 text-amber-700 text-xs">{initials(c.candidate_name)}</AvatarFallback></Avatar>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{c.candidate_name}</p>
              <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                <span className="truncate">{c.candidate_email}</span>
                <span className="px-1.5 py-0.5 rounded bg-muted capitalize">{c.source === "manual" ? "Added by admin" : c.source === "import" ? "Imported" : "Purchased"}</span>
                {c.attendance && <span className="capitalize">· {c.attendance}</span>}
              </div>
            </div>
            <AttendanceButtons value={c.attendance} onMark={(a) => markCandidate(c.session_id, a)} id={`cand-${c.session_id}`} />
          </div>
        ))}
        {roster.map((r) => (
          <div key={r.id} className="flex items-center gap-4 p-4" data-testid={`roster-row-${r.id}`}>
            <Avatar className="h-10 w-10"><AvatarFallback className="bg-primary/10 text-primary text-xs">{initials(r.user_name)}</AvatarFallback></Avatar>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{r.user_name}</p>
              <div className="flex items-center gap-2 mt-0.5">
                <StatusBadge status={r.status} />
                {r.attendance !== "none" && <span className="text-xs capitalize text-muted-foreground">· {r.attendance}</span>}
              </div>
            </div>
            <div className="flex gap-1.5 shrink-0">
              <Button size="sm" variant={r.attendance === "present" ? "default" : "outline"} onClick={() => mark(r.id, "present")} data-testid={`attendance-mark-present-${r.id}`} className={r.attendance === "present" ? "bg-emerald-600 hover:bg-emerald-700" : ""}><Check className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Present</span></Button>
              <Button size="sm" variant={r.attendance === "absent" ? "default" : "outline"} onClick={() => mark(r.id, "absent")} data-testid={`attendance-mark-absent-${r.id}`} className={r.attendance === "absent" ? "bg-red-600 hover:bg-red-700" : ""}><X className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Absent</span></Button>
              <Button size="sm" variant={r.attendance === "excused" ? "default" : "outline"} onClick={() => mark(r.id, "excused")} data-testid={`attendance-mark-excused-${r.id}`}><MinusCircle className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Excused</span></Button>
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}

function AttendanceButtons({ value, onMark, id }) {
  return (
    <div className="flex gap-1.5 shrink-0">
      <Button size="sm" variant={value === "present" ? "default" : "outline"} onClick={() => onMark("present")} data-testid={`attendance-mark-present-${id}`} className={value === "present" ? "bg-emerald-600 hover:bg-emerald-700" : ""}><Check className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Present</span></Button>
      <Button size="sm" variant={value === "absent" ? "default" : "outline"} onClick={() => onMark("absent")} data-testid={`attendance-mark-absent-${id}`} className={value === "absent" ? "bg-red-600 hover:bg-red-700" : ""}><X className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Absent</span></Button>
      <Button size="sm" variant={value === "excused" ? "default" : "outline"} onClick={() => onMark("excused")} data-testid={`attendance-mark-excused-${id}`}><MinusCircle className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Excused</span></Button>
    </div>
  );
}
